// Pure pieces of the client telemetry probe: the enable rule, the transition
// ring, the heartbeat gap rule, stack trimming and the persisted prior-session
// record. No DOM, no Angular, so the React bundle's vitest covers them.

export const TELEMETRY_EVENT = 'client-telemetry';
export const STORAGE_KEY = 'pm_client_telemetry';
export const TRANSITION_RING_SIZE = 8;
export const HEARTBEAT_MS = 1000;
export const STALL_GAP_MS = 4000;
export const MAX_EVENTS_PER_SESSION = 10;
export const MAX_ERROR_EVENTS = 3;
export const STACK_FRAMES = 6;

export interface Transition {
  url: string;
  at: number;
  via: string;
  stack?: string | null;
  note?: string;
}

export interface Tap {
  at: number;
  target: string;
  path: string;
  via?: string;
}

export interface PersistedSession {
  path: string;
  started_at: number;
  last_hb_at: number;
  ended: boolean;
  transitions: Transition[];
  last_tap: Tap | null;
}

// Default OFF: only an explicit "true" / "1" arms the probe.
export const telemetryEnabled = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const s = value.trim().toLowerCase();
  return s === 'true' || s === '1';
};

export const pushTransition = (
  ring: Transition[],
  entry: Transition,
  size: number = TRANSITION_RING_SIZE,
): Transition[] => {
  const next = ring.concat([entry]);
  return next.length > size ? next.slice(next.length - size) : next;
};

// A heartbeat that arrives `threshold` or more after the previous one means
// the main thread was blocked (or the page was suspended) for that long.
export const stallGap = (lastAt: number, now: number, threshold: number = STALL_GAP_MS): number | null => {
  if (!lastAt) return null;
  const gap = now - lastAt;
  return gap >= threshold ? gap : null;
};

export const trimStack = (stack: unknown, frames: number = STACK_FRAMES): string | null => {
  if (typeof stack !== 'string' || !stack) return null;
  const lines = stack
    .split('\n')
    .map(line => line.trim())
    .filter(line => !!line);
  // V8 leads with "Error: message"; WebKit lists frames only.
  if (lines.length && /^\w*Error\b/.test(lines[0]) && !/@|\bat\b/.test(lines[0])) lines.shift();
  const kept = lines.slice(0, frames).map(line => (line.length > 200 ? line.slice(0, 200) : line));
  return kept.length ? kept.join('\n') : null;
};

// Known noise that would otherwise spend the error budget every session:
// gtag under zone.js's Promise rejects on Promise.allSettled in every engine.
export const ignoredError = (message: string): boolean => /Promise\.allSettled/.test(message || '');

export const truncate = (value: unknown, max: number): string => {
  const s = value === undefined || value === null ? '' : String(value);
  return s.length > max ? s.slice(0, max) : s;
};

export interface TargetLike {
  tagName?: string;
  id?: string;
  className?: unknown;
  textContent?: string | null;
}

// "ION-BUTTON#send.primary 'Write a letter'" for the tap record.
export const describeTarget = (el: TargetLike | null | undefined): string => {
  if (!el || !el.tagName) return '';
  let out = String(el.tagName).toUpperCase();
  if (el.id) out += '#' + el.id;
  const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).filter(c => !!c) : [];
  if (cls.length) out += '.' + cls.slice(0, 2).join('.');
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) out += " '" + truncate(text, 30) + "'";
  return truncate(out, 100);
};

export const parsePriorSession = (raw: string | null, now: number): (PersistedSession & { age_ms: number }) | null => {
  if (!raw) return null;
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || typeof parsed.last_hb_at !== 'number') return null;
  return {
    path: typeof parsed.path === 'string' ? parsed.path : '',
    started_at: typeof parsed.started_at === 'number' ? parsed.started_at : 0,
    last_hb_at: parsed.last_hb_at,
    ended: parsed.ended === true,
    transitions: Array.isArray(parsed.transitions) ? parsed.transitions : [],
    last_tap: parsed.last_tap && typeof parsed.last_tap === 'object' ? parsed.last_tap : null,
    age_ms: now - parsed.last_hb_at,
  };
};
